import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';
// import { morph } from '@theme/morph';

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;

  connectedCallback() {
    this.addEventListener('change', this.variantChanged.bind(this));
    // Sélecteurs de dimensions enchaînés : la ligne des largeurs n'est pas une
    // option Shopify, c'est un filtre d'affichage qui déplace le vrai bouton radio.
    this.addEventListener('click', this.dimensionWidthClicked.bind(this));
  }

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Met à jour l'option sélectionnée dans l'interface
    this.updateSelectedOption(event.target);

    // Identifiant de la valeur d'option que la cliente vient de choisir. C'est celle
    // qu'on ne touchera jamais pendant le rattrapage d'une combinaison impossible.
    const changedOptionValueId = this.optionValueIdOf(event.target);

    // Déclenche un événement personnalisé pour notifier la sélection
    this.dispatchEvent(new VariantSelectedEvent({ id: changedOptionValueId }));

    // Lance la requête pour mettre à jour la section
    this.fetchUpdatedSection(this.buildRequestUrl(), changedOptionValueId);

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    this.updateUrl(event.target);
  }

  // Identifiant de valeur d'option porté par un bouton radio ou par un menu déroulant.
  optionValueIdOf(target) {
    if (target instanceof HTMLSelectElement) {
      return target.options[target.selectedIndex]?.dataset.optionValueId ?? '';
    }
    return target.dataset?.optionValueId ?? '';
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    // Si c'est une chaîne, trouve l'élément correspondant pour récupérer l'ID de la valeur d'option
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Met à jour l'état "checked" pour les boutons radio
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    // Met à jour l'attribut "selected" pour les éléments select
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      // Supprime l'attribut selected de toutes les options
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      // Ajoute l'attribut selected à la nouvelle option
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl() {
    let productUrl = this.dataset.productUrl;

    // On utilise la nouvelle API de Shopify pour récupérer les données de variante avec option_values.
    const params = [];
    params.push(`option_values=${this.selectedOptionsValues.join(',')}`);

    // Si variant-picker est un enfant de quick-add-component ou swatches-variant-picker-component,
    // nous devons ajouter section_id=section-rendering-product-card à l'URL
    // if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
    //   if (productUrl?.includes('?')) {
    //     productUrl = productUrl.split('?')[0];
    //   }
    //   return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    // }
    return `${productUrl}?${params.join('&')}`;
  }

  // Récupère la section mise à jour depuis le serveur.
  fetchUpdatedSection(requestUrl, changedOptionValueId = '', rescueDepth = 0) {
    // Annule la requête fetch précédente si elle est encore en cours
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Récupère les données JSON de la variante depuis la réponse
        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        // Met à jour seulement le variant picker
        this.updateVariantPicker(html);

        // Le serveur renvoie "null" quand la combinaison choisie n'existe pas.
        // Sans rattrapage, le prix devient NaN et le bouton d'achat est inutilisable.
        let variant = null;
        try {
          variant = JSON.parse(textContent);
        } catch (error) {
          variant = null;
        }

        if (!variant) {
          // Premier essai : déplacer les options que le serveur vient de barrer.
          if (rescueDepth === 0 && this.repairSelection(changedOptionValueId)) {
            this.fetchUpdatedSection(this.buildRequestUrl(), changedOptionValueId, 1);
            return;
          }

          // Second essai, pour les fiches à trois axes où le serveur ne barre que
          // l'option touchée : on ne garde que la valeur choisie et on laisse
          // Shopify désigner la première combinaison qui existe avec elle.
          if (rescueDepth < 2 && changedOptionValueId) {
            const productUrl = this.dataset.productUrl ?? '';
            this.fetchUpdatedSection(
              `${productUrl}?option_values=${changedOptionValueId}`,
              changedOptionValueId,
              2,
            );
            return;
          }

          // Tant qu'aucune variante n'est résolue, on ne propage rien : mieux vaut
          // garder le dernier prix affiché qu'en afficher un faux.
          return;
        }

        // Déclenche un événement pour notifier la mise à jour de la variante
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdatedEvent(variant, this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
            }),
          );
        }

        this.syncVariantInUrl(variant);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log("Fetch interrompu par l'utilisateur");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Rattrape une sélection impossible. On conserve la valeur que la cliente vient
   * de choisir, et on déplace les autres options vers leur première valeur réellement
   * disponible. Renvoie true si quelque chose a bougé.
   * @param {string} keepId - Identifiant de la valeur d'option à ne pas toucher.
   */
  repairSelection(keepId) {
    let repaired = false;

    const available = (element) => element.getAttribute('aria-disabled') !== 'true';
    // Sur les dimensions enchaînées, on préfère rester dans la largeur affichée.
    const visible = (element) => !element.closest('[hidden]');

    // Menus déroulants
    for (const select of this.querySelectorAll('select')) {
      const options = Array.from(select.options);
      const current = options.find((option) => option.hasAttribute('selected'));
      if (current && (current.dataset.optionValueId === keepId || available(current))) continue;

      const fallback = options.find(available);
      if (!fallback || fallback === current) continue;

      for (const option of options) option.removeAttribute('selected');
      fallback.setAttribute('selected', 'selected');
      select.value = fallback.value;
      repaired = true;
    }

    // Boutons radio
    for (const fieldset of this.querySelectorAll('fieldset')) {
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      if (inputs.length === 0) continue;

      const current = inputs.find((input) => input.checked);
      if (current && (current.dataset.optionValueId === keepId || available(current))) continue;

      const fallback =
        inputs.find((input) => available(input) && visible(input)) || inputs.find(available);
      if (!fallback || fallback === current) continue;

      if (current) current.checked = false;
      fallback.checked = true;
      this.revealDimensionOf(fallback);
      repaired = true;
    }

    return repaired;
  }

  // Aligne le paramètre variant de l'URL sur la variante réellement résolue.
  syncVariantInUrl(variant) {
    if (!variant?.id) return;
    if (typeof Theme === 'undefined' || Theme.template?.name !== 'product') return;
    if (this.closest('product-card') || this.closest('quick-add-dialog')) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === String(variant.id)) return;

    url.searchParams.set('variant', String(variant.id));
    history.replaceState({}, '', url.toString());
  }

  // ---- Dimensions enchaînées : largeur d'abord, puis les hauteurs qui existent ----

  // Clic sur une pastille de largeur.
  dimensionWidthClicked(event) {
    if (!(event.target instanceof Element)) return;

    const chip = event.target.closest('[data-dim-width-chip]');
    if (!chip || !this.contains(chip)) return;

    event.preventDefault();
    this.selectDimensionWidth(chip.dataset.dimW ?? '');
  }

  /**
   * Affiche les seules hauteurs qui existent dans cette largeur, et bascule la
   * sélection sur la hauteur disponible la plus proche de celle déjà choisie.
   * @param {string} width - La largeur demandée, en centimètres.
   */
  selectDimensionWidth(width) {
    const group = this.querySelector('[data-dim-heights]');
    if (!group || width === '') return;

    const checked = group.querySelector('input[type="radio"]:checked');
    const currentHeight = Number.parseInt(checked?.closest('[data-dim-h]')?.dataset.dimH ?? '0', 10);

    let target = null;
    let bestGap = Number.POSITIVE_INFINITY;

    for (const cell of group.querySelectorAll('[data-dim-w][data-dim-h]')) {
      const matches = cell.dataset.dimW === width;
      cell.toggleAttribute('hidden', !matches);
      if (!matches) continue;

      const input = cell.querySelector('input[type="radio"]');
      if (!input) continue;

      // Une hauteur épuisée reste choisissable, mais elle passe après toutes les autres.
      const penalty = input.getAttribute('aria-disabled') === 'true' ? 100000 : 0;
      const gap = Math.abs(Number.parseInt(cell.dataset.dimH ?? '0', 10) - currentHeight) + penalty;

      if (gap < bestGap) {
        bestGap = gap;
        target = input;
      }
    }

    for (const chip of this.querySelectorAll('[data-dim-width-chip]')) {
      chip.setAttribute('aria-pressed', chip.dataset.dimW === width ? 'true' : 'false');
    }

    const label = this.querySelector('[data-dim-width-value]');
    if (label) label.textContent = `${width} cm`;

    if (!target || target.checked) return;

    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Après un rattrapage, la hauteur retenue peut appartenir à une autre largeur.
  revealDimensionOf(input) {
    const cell = input.closest('[data-dim-w][data-dim-h]');
    if (!cell) return;

    const width = cell.dataset.dimW ?? '';
    const group = this.querySelector('[data-dim-heights]');
    if (!group) return;

    for (const other of group.querySelectorAll('[data-dim-w][data-dim-h]')) {
      other.toggleAttribute('hidden', other.dataset.dimW !== width);
    }
    for (const chip of this.querySelectorAll('[data-dim-width-chip]')) {
      chip.setAttribute('aria-pressed', chip.dataset.dimW === width ? 'true' : 'false');
    }

    const label = this.querySelector('[data-dim-width-value]');
    if (label) label.textContent = `${width} cm`;
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    // Trouve le nouveau variant picker dans la réponse
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }

    // Met à jour le variant picker
    this.innerHTML = newVariantPickerSource.innerHTML;
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(target) {
    // Détermine si nous sommes sur une page produit (pas dans une carte produit ou dialogue)
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Prépare l'URL pour la mise à jour de l'historique du navigateur
    const url = new URL(window.location.href);

    let variantId;

    // Extrait l'ID de la variante selon le type d'élément (radio ou select)
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      variantId = target.dataset.variantId || null;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Met à jour l'URL dans l'historique du navigateur si elle a changé
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }

    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("Aucun ID de valeur d'option trouvé");

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
